import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { extractData, extractMessage, extractErrorMessage, ApiResponse } from '../../../helpers/api-response.helper';

@Component({
  selector: 'app-quote-list',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './quote-list.component.html',
  styleUrls: ['./quote-list.component.css']
})
export class QuoteListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tableContainer') tableContainer?: ElementRef<HTMLDivElement>;
  
  quotes: any[] = [];
  filteredQuotes: any[] = [];
  loading = false;
  selectedQuote: any = null;
  isSupplier = false;

  // Pagination (Infinite Scroll)
  currentPage = 1;
  itemsPerPage = 6; // Load 6 quotes per page; ~5 visible in viewport, scroll for next 6
  totalPages = 1;
  totalItems = 0;
  isLoadingMore = false; // Track if loading more data
  hasMoreData = true; // Track if there's more data to load

  // Search and filter
  searchTerm = '';
  selectedCategory = '';
  selectedStatus = '';
  categories: any[] = [];

  private scrollHandler: any = null;
  private scrollableParent: HTMLElement | null = null;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private router: Router,
    private elementRef: ElementRef
  ) {
    this.isSupplier = this.authService.isSupplier();
  }

  ngOnInit() {
    this.loadCategories();
    this.loadQuotes();
    // Wait for view to initialize, then attach scroll listener
    setTimeout(() => {
      this.attachScrollListener();
    }, 100);
  }

  ngAfterViewInit() {
    // Attach scroll listener after view is initialized
    this.attachScrollListener();
  }

  ngOnDestroy() {
    if (this.scrollHandler) {
      if (this.scrollableParent) {
        this.scrollableParent.removeEventListener('scroll', this.scrollHandler);
      }
      if (this.tableContainer?.nativeElement) {
        this.tableContainer.nativeElement.removeEventListener('scroll', this.scrollHandler);
      }
    }
  }

  /** Find the scrollable ancestor (e.g. main-content) so we can listen to page scroll */
  private findScrollableParent(): HTMLElement | null {
    let el: HTMLElement | null = this.elementRef.nativeElement;
    while (el && el !== document.body) {
      el = el.parentElement;
      if (!el) break;
      const style = getComputedStyle(el);
      const overflowY = style.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        return el;
      }
    }
    return null;
  }

  attachScrollListener() {
    if (!this.scrollHandler) {
      this.scrollHandler = this.throttle(() => this.onScroll(), 200);
    }

    // Listen to the scrollable page container (main-content) so scrolling the page triggers load more
    if (!this.scrollableParent) {
      this.scrollableParent = this.findScrollableParent();
    }
    if (this.scrollableParent) {
      this.scrollableParent.removeEventListener('scroll', this.scrollHandler);
      this.scrollableParent.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    // Also listen to table container scroll (inner box) when it exists
    if (this.tableContainer?.nativeElement) {
      this.tableContainer.nativeElement.removeEventListener('scroll', this.scrollHandler);
      this.tableContainer.nativeElement.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
  }

  // Throttle function to limit scroll event calls
  private throttle(func: Function, limit: number) {
    let inThrottle: boolean;
    return function(this: any, ...args: any[]) {
      if (!inThrottle) {
        func.apply(this, ...args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  onScroll() {
    const threshold = 150;

    const isNearBottom = (el: HTMLElement): boolean => {
      const scrollTop = el.scrollTop;
      const scrollHeight = el.scrollHeight;
      const clientHeight = el.clientHeight;
      return scrollHeight - (scrollTop + clientHeight) <= threshold;
    };

    let nearBottom = false;
    if (this.scrollableParent && this.scrollableParent.scrollHeight > this.scrollableParent.clientHeight) {
      nearBottom = isNearBottom(this.scrollableParent);
    }
    if (!nearBottom && this.tableContainer?.nativeElement) {
      const tc = this.tableContainer.nativeElement;
      if (tc.scrollHeight > tc.clientHeight) {
        nearBottom = isNearBottom(tc);
      }
    }

    if (nearBottom && this.hasMoreData && !this.isLoadingMore && !this.loading) {
      this.loadMoreQuotes();
    }
  }

  loadCategories() {
    // Load all categories from the API (without pagination to get all)
    this.apiService.getCategories(1, 1000).subscribe({
      next: (response: ApiResponse) => {
        const data = extractData(response);
        if (data && Array.isArray(data.categories)) {
          // Backend returns { categories: [...], pagination: {...} }
          this.categories = data.categories.map((cat: any) => ({ 
            name: cat.name || '',
            _id: cat._id 
          })).filter((cat: any) => cat.name);
        } else if (Array.isArray(data)) {
          // If response is directly an array
          this.categories = data.map((cat: any) => ({ 
            name: cat.name || '',
            _id: cat._id 
          })).filter((cat: any) => cat.name);
        } else if (data && Array.isArray(data.data)) {
          // If response has a data property
          this.categories = data.data.map((cat: any) => ({ 
            name: cat.name || '',
            _id: cat._id 
          })).filter((cat: any) => cat.name);
        }
      },
      error: (err: ApiResponse) => {
        console.error('Error loading categories:', err);
        // Fallback to extracting from quotes if API fails
        this.extractCategories();
      }
    });
  }

  loadQuotes() {
    this.loading = true;
    this.currentPage = 1; // Reset to first page
    this.hasMoreData = true; // Reset has more data flag
    console.log("this.isSupplier",this.isSupplier)
    if (this.isSupplier) {
      // For suppliers, load active quotes (Active and Expired)
      this.apiService.getActiveQuotes(this.currentPage, this.itemsPerPage, this.searchTerm).subscribe({
        next: (response: ApiResponse) => {
          // Extract data from standardized response
          const data = extractData(response);
          if (data.quotes && data.pagination) {
      console.log("data.quotes",data.quotes)
            this.quotes = Array.isArray(data.quotes) ? data.quotes : [];
            this.currentPage = data.pagination.currentPage;
            this.totalPages = data.pagination.totalPages;
            this.totalItems = data.pagination.totalItems;
            this.hasMoreData = this.currentPage < this.totalPages;
            this.applyFilters();
            // Re-attach scroll listener after data loads
            setTimeout(() => {
              this.attachScrollListener();
            }, 50);
          } else {
            // Fallback for old API format (array response)
            this.quotes = Array.isArray(data) ? data : [];
            this.filteredQuotes = [...this.quotes];
            this.hasMoreData = false;
          }
          this.loading = false;
        },
        error: (err: ApiResponse) => {
          console.error('Error loading quotes:', err);
          const errorMessage = extractErrorMessage(err);
          console.error('Error message:', errorMessage);
          this.loading = false;
        }
      });
    } else {
      // For admin/manager, load all quotes
      this.apiService.getQuotes(undefined, undefined, undefined, this.currentPage, this.itemsPerPage, this.searchTerm).subscribe({
        next: (response: ApiResponse) => {
          // Extract data from standardized response
          const data = extractData(response);
          console.log("ssssssss",data);
          if (data.quotes && data.pagination) {
            this.quotes = Array.isArray(data.quotes) ? data.quotes : [];
            this.currentPage = data.pagination.currentPage;
            this.totalPages = data.pagination.totalPages;
            this.totalItems = data.pagination.totalItems;
            this.hasMoreData = this.currentPage < this.totalPages;
            this.applyFilters();
            // Re-attach scroll listener after data loads
            setTimeout(() => {
              this.attachScrollListener();
            }, 50);
          } else {
            // Fallback for old API format (array response)
            this.quotes = Array.isArray(data) ? data : [];
            this.filteredQuotes = [...this.quotes];
            this.hasMoreData = false;
          }
          this.loading = false;
        },
        error: (err: ApiResponse) => {
          console.error('Error loading quotes:', err);
          const errorMessage = extractErrorMessage(err);
          console.error('Error message:', errorMessage);
          this.loading = false;
        }
      });
    }
  }

  loadMoreQuotes() {
    // Don't load if already loading or no more data
    if (this.isLoadingMore || !this.hasMoreData || this.loading) {
      return;
    }

    // Check if there are more pages
    if (this.currentPage < this.totalPages) {
      this.isLoadingMore = true;
      const nextPage = this.currentPage + 1;
      
      if (this.isSupplier) {
        // For suppliers, load active quotes
        this.apiService.getActiveQuotes(nextPage, this.itemsPerPage, this.searchTerm).subscribe({
          next: (response: ApiResponse) => {
            const data = extractData(response);
            if (data.quotes && data.pagination) {
              const newQuotes = Array.isArray(data.quotes) ? data.quotes : [];
              // Append new quotes to existing ones
              this.quotes = [...this.quotes, ...newQuotes];
              this.currentPage = data.pagination.currentPage;
              this.totalPages = data.pagination.totalPages;
              this.totalItems = data.pagination.totalItems;
              this.hasMoreData = this.currentPage < this.totalPages;
              this.applyFilters();
              // Re-attach scroll listener after new data loads
              setTimeout(() => {
                this.attachScrollListener();
              }, 50);
            }
            this.isLoadingMore = false;
          },
          error: (err: ApiResponse) => {
            console.error('Error loading more quotes:', err);
            this.isLoadingMore = false;
          }
        });
      } else {
        // For admin/manager, load all quotes
        this.apiService.getQuotes(undefined, undefined, undefined, nextPage, this.itemsPerPage, this.searchTerm).subscribe({
          next: (response: ApiResponse) => {
            const data = extractData(response);
            if (data.quotes && data.pagination) {
              const newQuotes = Array.isArray(data.quotes) ? data.quotes : [];
              // Append new quotes to existing ones
              this.quotes = [...this.quotes, ...newQuotes];
              this.currentPage = data.pagination.currentPage;
              this.totalPages = data.pagination.totalPages;
              this.totalItems = data.pagination.totalItems;
              this.hasMoreData = this.currentPage < this.totalPages;
              this.applyFilters();
              // Re-attach scroll listener after new data loads
              setTimeout(() => {
                this.attachScrollListener();
              }, 50);
            }
            this.isLoadingMore = false;
          },
          error: (err: ApiResponse) => {
            console.error('Error loading more quotes:', err);
            this.isLoadingMore = false;
          }
        });
      }
    } else {
      this.hasMoreData = false;
    }
  }

  extractCategories() {
    const categorySet = new Set<string>();
    this.quotes.forEach(quote => {
      if (quote.product?.category?.name) {
        categorySet.add(quote.product.category.name);
      }
    });
    this.categories = Array.from(categorySet).map(name => ({ name }));
  }

  onSearch() {
    // Reload quotes with new search term (pagination will reset)
    this.loadQuotes();
  }

  onCategoryChange() {
    this.applyFilters();
  }

  onStatusChange() {
    this.applyFilters();
  }

  applyFilters() {
    let filtered = [...this.quotes];
    
    // Search filter
    if (this.searchTerm && this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase().trim();
      filtered = filtered.filter(quote => {
        const quoteId = (quote.quoteId || '').toLowerCase();
        const productId = (quote.product?.productId || '').toLowerCase();
        const productName = (quote.product?.name || '').toLowerCase();
        return quoteId.includes(searchLower) || 
               productId.includes(searchLower) || 
               productName.includes(searchLower);
      });
    }

    // Category filter - check both quote.category and product.category
    if (this.selectedCategory && this.selectedCategory.trim()) {
      filtered = filtered.filter(quote => {
        const categoryName = quote.category?.name || quote.product?.category?.name || '';
        return categoryName === this.selectedCategory;
      });
    }

    // Status filter - check supplierValidDateStatus for Active/Expired
    if (this.selectedStatus && this.selectedStatus.trim()) {
      filtered = filtered.filter(quote => {
        if (this.selectedStatus === 'Active') {
          return quote.supplierValidDateStatus === 'Active';
        } else if (this.selectedStatus === 'Expired') {
          return quote.supplierValidDateStatus === 'Expired';
        } else {
          return quote.status === this.selectedStatus;
        }
      });
    }

    this.filteredQuotes = filtered;
  }

  resetFilters() {
    this.searchTerm = '';
    this.selectedCategory = '';
    this.selectedStatus = '';
    this.loadQuotes();
  }

  viewQuote(quote: any) {
    // Navigate to quote details view for both supplier and admin
    this.router.navigate(['/quote/view', quote._id]);
  }

  formatDate(date: string | Date): string {
    if (!date) return '-';
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }

  getStatusClass(status: string): string {
    if (status === 'Active' || status === 'Submitted') {
      return 'status-quoted';
    } else if (status === 'Expired') {
      return 'status-expired';
    }
    return 'status-default';
  }

  getStatusText(status: string): string {
    if (status === 'Active' || status === 'Submitted') {
      return 'Quoted';
    }
    return status;
  }

  getSupplierValidDateStatusClass(supplierValidDateStatus: string): string {
    if (supplierValidDateStatus === 'Active') {
      return 'status-quoted';
    } else if (supplierValidDateStatus === 'Expired') {
      return 'status-expired';
    }
    return 'status-default';
  }

  getSupplierValidDateStatusText(supplierValidDateStatus: string): string {
    if (supplierValidDateStatus === 'Active') {
      return 'Active';
    } else if (supplierValidDateStatus === 'Expired') {
      return 'Expired';
    }
    return '-';
  }

  getShippingAddress(quote: any): string {
    const shipping = quote.product?.shippingDetails || quote.product?.shippingAddress;
    if (!shipping) return '-';
    
    const parts = [
      shipping.city,
      shipping.state,
      shipping.country || 'INDIA',
      shipping.pincode ? `PIN: ${shipping.pincode}` : ''
    ].filter(Boolean);
    
    return parts.join(', ') + '.';
  }

  getShippingTerm(quote: any): string {
    const shipping = quote.product?.shippingDetails || quote.product?.shippingAddress;
    return shipping?.shippingTerm?.toUpperCase() || '-';
  }
  
  viewSupplier(supplier: any) {
    // Navigate to supplier view details page
    const supplierId = supplier._id || supplier.id || '';
    this.router.navigate(['/supplier/view', supplierId]);
    // this.closeActionMenu();
  }

}

